import React, { type FC } from 'react'
import {
  StyledIconButton,
  StyledSortButtonsContainer,
  StyledTableHead,
  StyledTableHeadCell,
  StyledTableRow,
  StyledTableTitle
} from './MetersTable.style'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { type Sort, SortOrder, SortType } from '../../../utils/sortUtils'

interface CustomTableHeadProps {
  idVisible: boolean
  labelVisible: boolean
  usageVisible: boolean
  onToggleUsageVisibility: () => void
  onToggleIdVisibility: () => void
  onToggleLabelVisibility: () => void
  onSortingChange: (sort: Sort) => void
}

export const CustomTableHead: FC<CustomTableHeadProps> = (props) => {
  const { t } = useTranslation()

  const {
    idVisible,
    labelVisible,
    usageVisible,
    onToggleIdVisibility,
    onToggleLabelVisibility,
    onToggleUsageVisibility,
    onSortingChange
  } = props

  const idToggleIcon = idVisible ? <VisibilityIcon/> : <VisibilityOffIcon/>
  const labelToggleIcon = labelVisible ? <VisibilityIcon/> : <VisibilityOffIcon/>
  const usageToggleIcon = usageVisible ? <VisibilityIcon/> : <VisibilityOffIcon/>

  return (
        <StyledTableHead>
            <StyledTableRow>
                <StyledTableHeadCell></StyledTableHeadCell>
                <StyledTableHeadCell>
                    <StyledTableTitle>{t('radio_id')}</StyledTableTitle>
                    <Tooltip title={t(idVisible ? 'toggleVisibilityOff' : 'toggleVisibilityOn')} arrow>
                        <StyledIconButton onClick={onToggleIdVisibility}>
                            {idToggleIcon}
                        </StyledIconButton>
                    </Tooltip>
                    <StyledSortButtonsContainer>
                        <ExpandLessIcon style={{ cursor: 'pointer' }} onClick={() => {
                          // sortAscendingByName
                          onSortingChange({ type: SortType.ByName, order: SortOrder.Ascending })
                        }}/>
                        <ExpandMoreIcon style={{ cursor: 'pointer' }} onClick={() => {
                          // sortDescendingByName
                          onSortingChange({ type: SortType.ByName, order: SortOrder.Descending })
                        }}/>
                    </StyledSortButtonsContainer>

                </StyledTableHeadCell>
                <StyledTableHeadCell>
                    <StyledTableTitle>{t('usage_location_number')}</StyledTableTitle>
                    <Tooltip title={t(usageVisible ? 'toggleVisibilityOff' : 'toggleVisibilityOn')} arrow>
                        <StyledIconButton onClick={onToggleUsageVisibility}>
                            {usageToggleIcon}
                        </StyledIconButton>
                    </Tooltip>
                    <StyledSortButtonsContainer>
                        <ExpandLessIcon style={{ cursor: 'pointer' }} onClick={() => {
                          // sortAscendingByLocation
                          onSortingChange({ type: SortType.ByLocation, order: SortOrder.Ascending })
                        }}/>
                        <ExpandMoreIcon style={{ cursor: 'pointer' }} onClick={() => {
                          // sortDescendingByLocation
                          onSortingChange({ type: SortType.ByLocation, order: SortOrder.Descending })
                        }}/>
                    </StyledSortButtonsContainer>
                </StyledTableHeadCell>
                <StyledTableHeadCell>
                    <StyledTableTitle>{t('labels.labels')}</StyledTableTitle>
                    <Tooltip title={t(labelVisible ? 'toggleVisibilityOff' : 'toggleVisibilityOn')} arrow>
                        <StyledIconButton onClick={onToggleLabelVisibility}>
                            {labelToggleIcon}
                        </StyledIconButton>
                    </Tooltip>
                </StyledTableHeadCell>
                <StyledTableHeadCell>
                    <StyledTableTitle>{t('alarms.alarms')}</StyledTableTitle>
                    <StyledSortButtonsContainer>
                        <ExpandLessIcon style={{ cursor: 'pointer' }} onClick={() => {
                          // sortAscendingByAlarmsStatus
                          onSortingChange({ type: SortType.ByAlarmStatus, order: SortOrder.Ascending })
                        }}/>
                        <ExpandMoreIcon style={{ cursor: 'pointer' }} onClick={() => {
                          // sortDescendingByAlarmsStatus
                          onSortingChange({ type: SortType.ByAlarmStatus, order: SortOrder.Descending })
                        }}/>
                    </StyledSortButtonsContainer>
                </StyledTableHeadCell>
                <StyledTableHeadCell>
                    <StyledTableTitle>{t('reading')}</StyledTableTitle>
                    <StyledSortButtonsContainer>
                        <ExpandLessIcon style={{ cursor: 'pointer' }} onClick={() => {
                          // sortAscendingByReading
                          onSortingChange({ type: SortType.ByReading, order: SortOrder.Ascending })
                        }}/>
                        <ExpandMoreIcon style={{ cursor: 'pointer' }} onClick={() => {
                          // sortDescendingByReading
                          onSortingChange({ type: SortType.ByReading, order: SortOrder.Descending })
                        }}/>
                    </StyledSortButtonsContainer>

                </StyledTableHeadCell>
                <StyledTableHeadCell>
                    <StyledTableTitle>{t('activation_date')}</StyledTableTitle>
                    <StyledSortButtonsContainer>
                        <ExpandLessIcon style={{ cursor: 'pointer' }} onClick={() => {
                          // sortAscendingByCoupling
                          onSortingChange({ type: SortType.ByFirstDataEntry, order: SortOrder.Ascending })
                        }}/>
                        <ExpandMoreIcon style={{ cursor: 'pointer' }} onClick={() => {
                          // sortDescendingByCoupling
                          onSortingChange({ type: SortType.ByFirstDataEntry, order: SortOrder.Descending })
                        }}/>
                    </StyledSortButtonsContainer>
                </StyledTableHeadCell>
                <StyledTableHeadCell>
                    <StyledTableTitle>{t('updated')}</StyledTableTitle>
                    <StyledSortButtonsContainer>
                        <ExpandLessIcon style={{ cursor: 'pointer' }} onClick={() => {
                          // sortAscendingByTimeUpdate
                          onSortingChange({ type: SortType.ByUpdatedAt, order: SortOrder.Ascending })
                        }}/>
                        <ExpandMoreIcon style={{ cursor: 'pointer' }} onClick={() => {
                          // sortDescendingByTimeUpdate
                          onSortingChange({ type: SortType.ByUpdatedAt, order: SortOrder.Descending })
                        }}/>
                    </StyledSortButtonsContainer>
                </StyledTableHeadCell>
                <StyledTableHeadCell>
                    <StyledTableTitle/>
                    <StyledSortButtonsContainer/>
                </StyledTableHeadCell>
            </StyledTableRow>
        </StyledTableHead>

  )
}
